import * as React from 'react'
import type { ToastActionElement, ToastProps } from '@/components/notifications/toast'

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 5000 // 5 seconds in milliseconds

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType['ADD_TOAST']
      toast: ToasterToast
    }
  | {
      type: ActionType['UPDATE_TOAST']
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType['DISMISS_TOAST']
      toastId?: ToasterToast['id']
    }
  | {
      type: ActionType['REMOVE_TOAST']
      toastId?: ToasterToast['id']
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const toastHoverStates = new Map<string, boolean>()

const addToRemoveQueue = (toastId: string, delay: number = TOAST_REMOVE_DELAY) => {
  // Clear existing timeout if any
  if (toastTimeouts.has(toastId)) {
    clearTimeout(toastTimeouts.get(toastId)!)
    toastTimeouts.delete(toastId)
  }

  // Don't add to queue if toast is being hovered
  if (toastHoverStates.get(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    })
  }, delay)

  toastTimeouts.set(toastId, timeout)
}

const pauseToast = (toastId: string) => {
  // Clear existing timeout
  if (toastTimeouts.has(toastId)) {
    clearTimeout(toastTimeouts.get(toastId)!)
    toastTimeouts.delete(toastId)
  }
  toastHoverStates.set(toastId, true)
}

const resumeToast = (toastId: string) => {
  toastHoverStates.set(toastId, false)
  // Restart the timeout with 5 seconds delay
  addToRemoveQueue(toastId, TOAST_REMOVE_DELAY)
}

// Export functions to control toast hover state
export const pauseToastOnHover = (toastId: string) => {
  pauseToast(toastId)
}

export const resumeToastOnLeave = (toastId: string) => {
  resumeToast(toastId)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case 'DISMISS_TOAST': {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
        case 'REMOVE_TOAST':
          if (action.toastId === undefined) {
            // Clean up all timeouts and hover states
            toastTimeouts.forEach((timeout) => clearTimeout(timeout))
            toastTimeouts.clear()
            toastHoverStates.clear()
            return {
              ...state,
              toasts: [],
            }
          }
          // Clean up timeout and hover state for this toast
          if (toastTimeouts.has(action.toastId)) {
            clearTimeout(toastTimeouts.get(action.toastId)!)
            toastTimeouts.delete(action.toastId)
          }
          toastHoverStates.delete(action.toastId)
          return {
            ...state,
            toasts: state.toasts.filter((t) => t.id !== action.toastId),
          }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, 'id'> & {
  duration?: number
}

function toast({ duration, ...props }: Toast) {
  const id = genId()
  const toastDuration = duration ?? TOAST_REMOVE_DELAY

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id })

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Auto-dismiss after duration
  addToRemoveQueue(id, toastDuration)

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  }
}

export { useToast, toast }

